/**
 * The Home page "Workflow" dropdown: it creates new forms, nothing else.
 * Rendered by `HomePageToolbar`.
 *
 * Every table-view command (save, rename, reset, delete, insert column, clear
 * filters, export) now lives in the tab bar's contextual view menu — see
 * `TableFilterButtons`. Duplicating them here is what made view management
 * unfindable, so this menu deliberately holds no orchestrator state.
 * The dropdown is 320px wide, capped to the viewport less `marginLG` per side.
 * Below `md` the menu is capped at half the dynamic viewport less one large
 * control and scrolls inside itself, so it fits on whichever side antd flips it to.
 */
import { FC, useState, useContext, useMemo, useCallback } from 'react';
import { Button, Dropdown, Grid, MenuProps, Tooltip, theme } from 'antd';
import Icon from '../../../atoms/Icon';
import { useNavigate } from 'react-router-dom';
import { useFormTypes } from '../../../../use/useFormTypes';
import { homePageConfig } from '#types';
import { createForm } from '../../../../services/artifactFunctions';
import { devFlag } from '../../../../config';
import { MessageContext } from '../../../../providers/MessageContext';

export interface HomePageWorkflowMenuProps {
  formTypes: ReturnType<typeof useFormTypes>;
  className?: string;
  style?: React.CSSProperties;
  additionalMenuItems?: MenuProps['items'];
}

const useHomePageWorkflowMenu = (
  formTypes: ReturnType<typeof useFormTypes>,
  additionalMenuItems?: MenuProps['items'],
) => {
  const { messageApi } = useContext(MessageContext);
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const handleCreateForm = useCallback(
    async (formTypeId: string) => {
      setIsCreating(true);
      try {
        const selectedFormType = Object.values(formTypes.data).find(
          (type) => type.docId === formTypeId,
        );
        const newFormId = await createForm({
          formType: formTypeId,
          title: `New ${selectedFormType?.description?.shortLabel || 'Form'}`,
        });
        navigate(`/forms/${newFormId}`);
      } catch (error) {
        console.error('Error creating new form:', error);
        messageApi.error('Failed to create new form');
      } finally {
        setIsCreating(false);
      }
    },
    [formTypes.data, navigate, messageApi],
  );

  const createFormItems: MenuProps['items'] = useMemo(() => {
    const allowedTypes = homePageConfig.workflowMenu.allowedFormTypes;
    const filteredFormTypes = Object.values(formTypes.data).filter(
      (formType) => {
        if (devFlag) return true;
        return formType.docId && allowedTypes.includes(formType.docId as any);
      },
    );

    if (filteredFormTypes.length === 0) {
      return [
        {
          key: 'no-forms',
          label: 'No form types available',
          disabled: true,
        },
      ];
    }

    return filteredFormTypes.map(
      (formType) =>
        ({
          key: formType.docId,
          label: (
            <Tooltip title={formType.description?.longDescription}>
              {formType.description?.shortLabel || 'Unnamed Form Type'}
            </Tooltip>
          ),
          onClick: () => handleCreateForm(formType.docId as any),
        }) as Required<MenuProps>['items'][number],
    );
  }, [formTypes.data, handleCreateForm]);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      {
        type: 'group',
        key: 'create-form-group',
        label: `Create New Form${devFlag ? '✶' : ''}`,
        children: createFormItems,
      },
    ];

    if (additionalMenuItems && additionalMenuItems.length > 0) {
      items.push(
        {
          key: 'divider-additional',
          type: 'divider' as const,
        },
        ...additionalMenuItems,
      );
    }

    return items;
  }, [createFormItems, additionalMenuItems]);

  return {
    menuItems,
    isCreating,
    handleCreateForm,
  };
};

export const HomePageWorkflowMenu: FC<HomePageWorkflowMenuProps> = ({
  formTypes,
  className,
  style,
  additionalMenuItems,
}) => {
  const { token } = theme.useToken();
  const { md } = Grid.useBreakpoint();
  const { menuItems, isCreating } = useHomePageWorkflowMenu(
    formTypes,
    additionalMenuItems,
  );

  return (
    <Dropdown
      menu={{
        items: menuItems,
        style: md
          ? undefined
          : {
              maxHeight: `calc(50dvh - ${token.controlHeightLG}px)`,
              overflowY: 'auto',
            },
      }}
      trigger={['click']}
      disabled={formTypes.loading || formTypes?.data.length === 0 || isCreating}
      styles={{
        root: { width: 320, maxWidth: `calc(100vw - ${token.marginLG * 2}px)` },
      }}
    >
      <Button
        disabled={
          formTypes.loading || formTypes?.data.length === 0 || isCreating
        }
        type="primary"
        loading={isCreating}
        className={className}
        icon={<Icon.ChevronsDown />}
        style={style}
        size="large"
      >
        Workflow
      </Button>
    </Dropdown>
  );
};

export default HomePageWorkflowMenu;
